/* eslint-disable  @typescript-eslint/ban-types, @typescript-eslint/await-thenable */
import { LocalVideoTrack } from 'twilio-video';
import { isSupported } from '@twilio/video-processors';

/**
 * Returns true if the current browser environment supports Twilio video processors.
 * Requires VideoFrame API and WebGL2 canvas context.
 */
export function checkVideoFrameSupport(): boolean {
  return Boolean(isSupported) && 'VideoFrame' in window && !!document.createElement('canvas').getContext('webgl2');
}

/**
 * Safely removes a processor from the video track.
 * Swallows errors to avoid cascading failures during effect transitions.
 */
export async function safeRemoveProcessor<T extends object>(videoTrack: LocalVideoTrack, processor: T | null): Promise<void> {
  if (!processor) return;

  try {
    await videoTrack.removeProcessor(processor as any);
  } catch (error) {
    console.warn('Failed to remove processor:', error);
  }
}

/**
 * Pre-loads an image URL into an HTMLImageElement.
 * Resolves immediately if the image is already complete.
 */
export function preloadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load background image: ${url}`));

    img.src = url;

    if (img.complete) resolve(img);
  });
}
